import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { HOME_INDICATOR_SPACE } from 'components/Main/Layout/TabBar'

// While the keyboard is up, iOS Safari shrinks only the visual viewport and
// pans it to the focused field, which would push a screen's top bar off the
// top. The screen follows the visual viewport then, so the bar stays and the
// composer sits on the keyboard. A pinch-zoomed page isn't a keyboard, nor a
// viewport a few points short, as iOS 26.0 leaves one after the keyboard.
const useKeyboardFrame = () => {
  const [frame, setFrame] = useState(null)
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return undefined
    const update = () => {
      const lost = document.documentElement.clientHeight - viewport.height
      const open = lost > 150 && Math.abs(viewport.scale - 1) < 0.01
      const top = Math.round(viewport.offsetTop)
      const height = Math.round(viewport.height)
      setFrame((frame) =>
        !open
          ? null
          : frame?.top === top && frame?.height === height
          ? frame
          : { top, height }
      )
    }
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [])
  return frame
}

// One screen of the chat page on a phone: the whole window, over the page
// and the nav, under the menus, dialogs and toasts it opens. `layer` stacks
// a screen over another.
export const Screen = ({ layer = 1, children }) => {
  const keyboard = useKeyboardFrame()
  const ref = useRef(null)
  const height = useRef(null)

  // The page under a screen holds still: it scrolls as a whole on a phone,
  // and a drag on the screen's bars would move the list beneath.
  useEffect(() => {
    const { style } = document.documentElement
    const before = style.overflow
    style.overflow = 'hidden'
    return () => {
      style.overflow = before
    }
  }, [])

  // As the screen shrinks for the keyboard, the message list scrolls by as
  // much, so the newest messages stay in view as in iOS Messages; and back
  // as it closes.
  useLayoutEffect(() => {
    const now = ref.current.clientHeight
    const list = ref.current.querySelector('.str-chat__list')
    if (list && height.current) list.scrollTop += height.current - now
    height.current = now
  }, [keyboard?.height])

  return (
    <Box
      ref={ref}
      style={
        keyboard
          ? {
              top: keyboard.top,
              bottom: 'auto',
              height: keyboard.height,
              paddingBottom: 0,
            }
          : undefined
      }
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.drawer + layer,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        // Clear of a Home Screen web app's home indicator, which the
        // keyboard covers when it's up.
        pb: `${HOME_INDICATOR_SPACE}px`,
        // Under 640px Stream's sample CSS sizes the channel by an
        // --app-height nothing sets, which leaves it as tall as its messages.
        '& .str-chat-channel': { height: '100%' },
        // The list's scroll stops at its ends rather than moving the page.
        '& .str-chat__list': { overscrollBehavior: 'contain' },
      }}
    >
      {children}
    </Box>
  )
}

// A screen's top bar: back, then what names the screen.
export const ScreenBar = ({ onBack, label, children }) => (
  <Box
    sx={{
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      minHeight: 56,
      pl: 0.5,
      pr: 1,
      borderBottom: 1,
      borderColor: 'divider',
    }}
  >
    <IconButton aria-label={label} onClick={onBack}>
      <ArrowBackIosNewIcon fontSize="small" />
    </IconButton>
    {children}
  </Box>
)
